/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { internalSymbol } from "../constants.mjs";
import { extend } from "../data/extend.mjs";
import { Pathfinder } from "../data/pathfinder.mjs";
import { Base } from "./base.mjs";
import { validateObject } from "./validate.mjs";

export { BaseWithOptions };

/**
 * This is the base class with options from which some monster classes are derived.
 *
 * This class is actually only used as a base class.
 *
 * Classes that require the possibility of options can be derived directly from this class.
 * Derived classes almost always override the `defaul` getter with their own values.
 *
 * @externalExample ../../example/types/basewithoptions.mjs
 * @license AGPLv3
 * @since 1.13.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 * @deprecated since 3.15.0 use {@link Monster.Types.Base} with {@link Monster.Types.equipWithInternal} instead.
 */
class BaseWithOptions extends Base {
	/**
	 *
	 * @param {object} options
	 */
	constructor(options) {
		super();

		if (options === undefined) {
			options = {};
		}

		this[internalSymbol] = extend({}, this.defaults, validateObject(options));
	}

	/**
	 * This getter provides the options. Derived classes overwrite
	 * this getter with their own values. It is good karma to always include
	 * the values from the parent class.
	 *
	 * ```javascript
	 * get defaults() {
	 *     return Object.assign({}, super.defaults, {
	 *         mykey: true
	 *     });
	 * }
	 *
	 * ```
	 *
	 * @return {object}
	 */
	get defaults() {
		return {};
	}

	/**
	 * nested options can be specified by path `a.b.c`
	 *
	 * @param {string} path
	 * @param {*} defaultValue
	 * @return {*}
	 * @since 1.10.0
	 */
	getOption(path, defaultValue) {
		let value;

		try {
			value = new Pathfinder(this[internalSymbol]).getVia(path);
		} catch (e) {}

		if (value === undefined) return defaultValue;
		return value;
	}
}
